import * as React from 'react'

import {
  Container,
  Dismiss,
  ExpandableContainer,
  ExpandableMessage,
  Message,
  ToggleButton,
} from './Alert.styles'

interface ICaretProps {
  direction: 'up' | 'down'
}

const Caret: React.FC<ICaretProps> = ({ direction }) => {
  const path = direction === 'up' ? 'M1 4L4 1L7 4' : 'M1 1L4 4L7 1'

  return (
    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ display: 'inline' }}>
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export type AlertKind = 'info' | 'success' | 'danger' | 'warning'

interface IProps {
  children: React.ReactNode
  kind: AlertKind
  onDismiss?: () => void
  expandable?: boolean
  expandText?: string
  collapseText?: string
}

interface IState {
  isOpen: boolean
  isExpanded: boolean
  isOverflowing: boolean
}

export default class Alert extends React.Component<IProps, IState> {
  public static defaultProps = {
    kind: 'info',
    expandable: false,
    expandText: 'Read more',
    collapseText: 'Show less',
  }

  public state = { isOpen: true, isExpanded: false, isOverflowing: false }
  private messageRef = React.createRef<HTMLDivElement>()

  public render() {
    if (!this.state.isOpen) return null

    const { kind, children, expandable, expandText, collapseText } = this.props
    const { isExpanded, isOverflowing } = this.state

    if (expandable) {
      const messageId = `alert-message-${Math.random().toString(36).substr(2, 9)}`
      const buttonId = `alert-toggle-${Math.random().toString(36).substr(2, 9)}`

      return (
        <ExpandableContainer kind={kind}>
          <ExpandableMessage
            kind={kind}
            isExpanded={isExpanded}
            id={messageId}
            role="region"
            aria-live="polite"
            ref={this.messageRef}
          >
            {children}
          </ExpandableMessage>
          {isOverflowing && (
            <ToggleButton
              id={buttonId}
              kind={kind}
              onClick={this.handleToggleExpand}
              onKeyDown={this.handleKeyDown}
              tabIndex={0}
              role="button"
              aria-expanded={isExpanded}
              aria-controls={messageId}
              aria-label={`${isExpanded ? collapseText : expandText}. Alert message: ${
                typeof children === 'string' ? children.substring(0, 100) : 'Alert content'
              }`}
            >
              {isExpanded ? (
                <>
                  {collapseText} <Caret direction="up" />
                </>
              ) : (
                <>
                  {expandText} <Caret direction="down" />
                </>
              )}
            </ToggleButton>
          )}
        </ExpandableContainer>
      )
    }

    return (
      <Container kind={kind}>
        <Message kind={kind}>{children}</Message>
        {kind !== 'warning' ? (
          <Dismiss kind={kind} onClick={this.handleDismiss}>
            Dismiss
          </Dismiss>
        ) : null}
      </Container>
    )
  }

  public componentDidMount() {
    if (this.props.expandable) {
      this.checkOverflow()
      window.addEventListener('resize', this.checkOverflow)
    }
  }

  public componentWillUnmount() {
    if (this.props.expandable) {
      window.removeEventListener('resize', this.checkOverflow)
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (this.props.expandable && prevProps.children !== this.props.children) {
      this.checkOverflow()
    }
  }

  private handleDismiss = () => {
    this.setState({ isOpen: false }, () => {
      if (!this.props.onDismiss) return
      this.props.onDismiss()
    })
  }

  private handleToggleExpand = () => {
    this.setState(prevState => ({ isExpanded: !prevState.isExpanded }))
  }

  private handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.handleToggleExpand()
    }
  }

  private checkOverflow = () => {
    if (!this.messageRef.current) return

    const element = this.messageRef.current
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
    const maxHeight = lineHeight * 2 // 2 lines

    // Temporarily expand to measure full height
    const originalWebkitLineClamp = element.style.webkitLineClamp
    const originalDisplay = element.style.display

    element.style.webkitLineClamp = 'unset'
    element.style.display = '-webkit-box'

    const fullHeight = element.scrollHeight

    // Restore original styles
    element.style.webkitLineClamp = originalWebkitLineClamp
    element.style.display = originalDisplay

    const isOverflowing = fullHeight > maxHeight + 2 // Add small tolerance

    if (isOverflowing !== this.state.isOverflowing) {
      this.setState({ isOverflowing })
    }
  }
}
